import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate, useParams } from 'react-router-dom';
import { useSearch } from '../Keyvault/SearchContext';
import { getInstanceList, decommissionInstance } from '../../apis/tenantDetailsApi/tenantDetailsApi';
import { Key } from '@mui/icons-material';
import { checkRolePermissions } from '../../utils/HasPermission';

interface InstanceItem {
  FE_Keys: string;
  Env_Id: string;
  Instance_Nm: string;
  Instance_Id: string;
  Tenant_Nm: string;
  Tenant_Id: string;
  Env_Nm: string;
  Keyvaultdetails_Id: string;
  Keyvault_Nm: string;
  Tenant_Status:string
  Instance_Status:string,
  EnvironmentInstanceCreated_Typ:string
  EnvironmentInstance_Typ : string
  ClonedFrom_Id:string
}

interface InstanceListProps {
  tenantId?: string;
  envId?: string;
}

const InstanceList: React.FC<InstanceListProps> = ({ tenantId, envId }) => {

  const isReadEnabled = checkRolePermissions("Instance", false, "read")
  const isUpdateEnabled = checkRolePermissions("Instance", false, "update")
  const isDeleteEnabled = checkRolePermissions("Instance", false, "delete")

  const { searchText } = useSearch();
  const [instances, setInstances] = useState<InstanceItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuInstance, setMenuInstance] = useState<InstanceItem | null>(null);
  const [isDecommissionOpen, setIsDecommissionOpen] = useState(false);
  const [decommissionLoading, setDecommissionLoading] = useState(false);
  const navigate = useNavigate();

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getInstanceList();
      // Ensure we handle the API response safely
      const instanceArray = Array.isArray(response.data) ? response.data : [];
      setInstances(
        instanceArray.map((item: any) => ({
          FE_Keys: item.EnvironmentInstanceFE_Keys,
          Env_Id: item.EnvironmentInstanceEnv_Id,
          Instance_Nm: item.EnvironmentInstance_Nm,
          Instance_Id: item.Id,
          Tenant_Nm: item.Tenant_Nm,
          Tenant_Id: item.EnvironmentInstanceTenant_Id,
          Env_Nm: item.EnvironmentInstance_Nm,
          Keyvault_Nm: item.Keyvault_Nm,
          Keyvaultdetails_Id: item.EnvironmentInstanceKeyVault_Id ?? '',
          Instance_Status : item?.Instance_Status || "-",
          Tenant_Status : item?.Tenant_Status || "-",
          EnvironmentInstanceCreated_Typ : item?.EnvironmentInstanceCreated_Typ || "-",
          EnvironmentInstance_Typ : item?.EnvironmentInstance_Typ,
          ClonedFrom_Id : item?.ClonedFrom_Id || "-"
        }))
      );
    } catch (err: any) {
      console.error('Error fetching instances:', err);
      setError('Failed to load instance list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    if(!isReadEnabled){
      setError("You dont have permission to view Instance List")
    }
  }, []);

  const filteredInstances = useMemo(() => {
    let result = instances;


    if (tenantId) {
      result = result.filter((inst) => inst.Tenant_Id === tenantId);
    }

    if (envId) {
      result = result.filter((inst) => inst.Env_Id === envId);
    }


    if (searchText) {
      result = result.filter((inst) =>
        Object.values(inst).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchText.toLowerCase())
        )
      );
    }

    return result;
  }, [instances, tenantId, envId, searchText]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>, inst: InstanceItem) => {
    setAnchorEl(event.currentTarget);
    setMenuInstance(inst);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePostProvision = () => {
    if (!menuInstance) return;
    handleClose();
    navigate(`/instances/configure/${menuInstance.Instance_Id}?name=${menuInstance.Env_Nm}`);
  };

  const handleDecommission = () => {
    setAnchorEl(null);
    setIsDecommissionOpen(true);
  };

  const handleCloseDecommissionModal = () => {
    setIsDecommissionOpen(false);
  };

  const confirmDecommissionAction = async () => {
    if (!menuInstance) return;

    setDecommissionLoading(true);
    try {
      await decommissionInstance(Number(menuInstance.Instance_Id));
      console.log("Decommissioned successfully:", menuInstance.Instance_Nm);
      handleCloseDecommissionModal();
      await loadData();
    } catch (err: any) {
      console.error("Decommissioning failed:", err);
      alert(`Failed to decommission: ${err.message}`);
    } finally {
      setDecommissionLoading(false);
    }
  };

  return (
    <Box sx={{ mb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" sx={{ color: '#333' }}>
          Instance List
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          sx={{
            backgroundColor: '#734bd1',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#5e3fb0' },
          }}
        >
          Refresh
        </Button>
      </Box>
      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress sx={{ color: '#734bd1' }} />
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography color="error">{error}</Typography>
            </Box>
          ) : filteredInstances.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography>No instances found</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}
              sx={{
                  maxHeight: 380, 
              }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {/* <TableCell sx={{ width: 180, backgroundColor: '#e0d3ff' }}>FE Keys</TableCell> */}
                    <TableCell sx={{ width: 120, backgroundColor: '#e0d3ff' }}>Instance ID</TableCell>
                    <TableCell sx={{ width: 150, backgroundColor: '#e0d3ff' }}>Instance Name</TableCell>
                    <TableCell sx={{ width: 200, backgroundColor: '#e0d3ff' }}>Tenant Name</TableCell>
                    <TableCell sx={{ width: 220, backgroundColor: '#e0d3ff' }}>Keyvault Name</TableCell>
                    <TableCell sx={{ width: 200, backgroundColor: '#e0d3ff' }}>Tenant Status</TableCell>
                    <TableCell sx={{ width: 150, backgroundColor: '#e0d3ff' }}>Instance Type</TableCell>
                     <TableCell sx={{ width: 200, backgroundColor: '#e0d3ff' }}>Clone from ID</TableCell>
                    <TableCell sx={{ width: 200, backgroundColor: '#e0d3ff' }}>Instance Status</TableCell>
                    <TableCell sx={{ width: 130, backgroundColor: '#e0d3ff' }} align="center"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInstances.map((inst, idx) => (
                    <TableRow key={idx} hover>
                      {/* <TableCell>{inst.FE_Keys}</TableCell> */}
                      <TableCell>{inst.Instance_Id}</TableCell>
                      <TableCell>{inst.Instance_Nm}</TableCell>
                      <TableCell>{inst.Tenant_Nm}</TableCell>
                      <TableCell>{inst.Keyvault_Nm}</TableCell>
                      <TableCell>{inst.Tenant_Status}</TableCell>
                      <TableCell>{inst.EnvironmentInstanceCreated_Typ}</TableCell>
                      <TableCell>{inst.ClonedFrom_Id}</TableCell>
                      <TableCell>{inst.Instance_Status}</TableCell>
                      {/* <TableCell>{inst.Keyvaultdetails_Id || <em style={{ color: '#999' }}>null</em>}</TableCell> */}
                      <TableCell align="center">
                        <IconButton onClick={(e) => handleOpen(e, inst)}>
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl) && menuInstance === inst}
                          onClose={handleClose}
                        >
                          <MenuItem>View</MenuItem>
                          {inst?.Instance_Status !== "Decommissioned" && <MenuItem>Permissions</MenuItem>}
                          {inst?.Instance_Status !== "Decommissioned" && inst?.Instance_Status !== "Provisioning Failed" &&<MenuItem onClick={isUpdateEnabled ? handlePostProvision : ()=>{}} style={{color: !isUpdateEnabled ? "#0000001f" : ""}}>Post Provision</MenuItem>}
                          {inst?.Instance_Status !== "Decommissioned" && inst?.Instance_Status !== "Provisioning Failed" && inst?.Instance_Status !== "Provisioning" && inst?.EnvironmentInstance_Typ !== "Production" && <MenuItem onClick={isDeleteEnabled ? handleDecommission : ()=>{}} sx={{ color: 'red' }} style={{color: !isDeleteEnabled ? "#0000001f" : ""}}> Decommission</MenuItem>}
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      {/* 3. Updated Decommission Confirmation Modal */}
      <Dialog
        open={isDecommissionOpen}
        onClose={decommissionLoading ? undefined : handleCloseDecommissionModal}
        PaperProps={{
          sx: { borderRadius: '16px', padding: '4px' }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="error" /> Confirm Action
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to decommission <strong>{menuInstance?.Instance_Nm}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ padding: 2 }}>
          <Button
            onClick={handleCloseDecommissionModal}
            color="inherit"
            disabled={decommissionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDecommissionAction}
            variant="contained"
            color="error"
            disabled={decommissionLoading}
            startIcon={decommissionLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {decommissionLoading ? 'Processing...' : 'Decommission'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstanceList;
